'use client';

import { forwardRef, useCallback, useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/cn';
import { SystemLoadingSurface } from './SystemLoading';
import styles from './AnimatedBar.module.css';

const COLORS = ['#4ABDE9', '#5782ee', '#7781e9', '#a073dc', '#35B87A'];

/** 所有参数滑杆与进度条共用参考稿的颜色插值。 */
export function barPosition(value: number, min = 0, max = 100) {
  return Number.isFinite(value) && max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0;
}

export function barColor(position: number) {
  const scaled = Math.min(1, Math.max(0, position)) * (COLORS.length - 1);
  const index = Math.min(COLORS.length - 2, Math.floor(scaled));
  const mix = scaled - index;
  const channels = (color: string) => color.slice(1).match(/../g)!.map((channel) => parseInt(channel, 16));
  const from = channels(COLORS[index]);
  const to = channels(COLORS[index + 1]);
  return `rgb(${from.map((channel, i) => Math.round(channel + (to[i] - channel) * mix)).join(', ')})`;
}

function barStyle(position: number, style?: CSSProperties): CSSProperties {
  return { '--bar-position': position, '--bar-color': barColor(position), ...style } as CSSProperties;
}

function BarTrack({ position, ticks = 5, slider = false }: { position: number; ticks?: number; slider?: boolean }) {
  return <>
    <span className={styles.track} aria-hidden="true">
      <span className={styles.fill} style={slider ? undefined : { width: `${position * 100}%` }} />
      {ticks > 1 && <span className={styles.dots}>{Array.from({ length: ticks }, (_, index) => (
        <i key={index} data-passed={index / (ticks - 1) <= position} />
      ))}</span>}
    </span>
    {slider && <span className={styles.thumb} aria-hidden="true" />}
  </>;
}

interface AnimatedRangeProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  ticks?: number;
  valueLabel?: ReactNode;
  label?: ReactNode;
  mascot?: boolean;
  size?: 'regular' | 'compact';
}

/** 保留原生 range 的键盘、表单与拖动语义，仅替换视觉层。 */
export const AnimatedRange = forwardRef<HTMLInputElement, AnimatedRangeProps>(function AnimatedRange({
  value, defaultValue, min = 0, max = 100, step = 1, onChange, className, style, disabled,
  ticks = 5, valueLabel, label, mascot = false, size = 'compact', onPointerDown, onPointerUp,
  onPointerMove, onPointerCancel, onLostPointerCapture, onBlur, ...inputProps
}, ref) {
  const t = useTranslations('common');
  const [internalValue, setInternalValue] = useState(Number(defaultValue ?? min));
  const position = barPosition(Number(value ?? internalValue), Number(min), Number(max));
  const isMax = position === 1;
  const isDisabled = disabled || Number(max) <= Number(min);
  const wasMax = useRef(isMax);
  const [reaction, setReaction] = useState(0);
  useEffect(() => {
    if (isMax && !wasMax.current && !disabled) setReaction((current) => current + 1);
    wasMax.current = isMax;
  }, [isMax, disabled]);
  const surface = useRef<HTMLSpanElement>(null);
  const drag = useRef<{ pointerId: number; left: number; travel: number; offset: number; rtl: boolean; position: number } | null>(null);
  const frame = useRef<number | null>(null);
  const finishDrag = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    drag.current = null;
    surface.current?.removeAttribute('data-dragging');
    surface.current?.style.removeProperty('--bar-drag-position');
    surface.current?.style.removeProperty('--bar-drag-color');
  }, []);
  useEffect(() => finishDrag, [finishDrag]);
  useEffect(() => { if (isDisabled) finishDrag(); }, [isDisabled, finishDrag]);
  const moveDrag = (clientX: number) => {
    const current = drag.current;
    if (!current) return;
    const physical = Math.min(1, Math.max(0, (clientX - current.left - current.offset) / current.travel));
    current.position = current.rtl ? 1 - physical : physical;
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      if (!drag.current || !surface.current) return;
      // Independent CSS variables survive controlled prop updates without a React render per pixel.
      surface.current.style.setProperty('--bar-drag-position', String(drag.current.position));
      surface.current.style.setProperty('--bar-drag-color', barColor(drag.current.position));
    });
  };

  return <span ref={surface} className={cn('w-full', styles.range, className)} style={barStyle(position, style)}
    data-max={isMax} data-disabled={isDisabled || undefined} data-size={size}>
    {label !== undefined ? <span className={styles.heading}>
      <span className={styles.label}>{label}</span>
      {valueLabel !== undefined && <span className={styles.value}>{valueLabel}</span>}
    </span> : valueLabel !== undefined && <span className={styles.value}>{valueLabel}</span>}
    <span className={styles.control}>
      {mascot && isMax && reaction > 0 && !disabled && <span key={reaction} className={styles.mascotWindow} aria-hidden="true">
        <span className={styles.mascot}><span className={styles.mascotArt}><i /><i /></span></span>
      </span>}
      <BarTrack position={position} ticks={ticks} slider />
      <input {...inputProps} ref={ref} type="range" min={min} max={max} step={step}
        value={value ?? internalValue} disabled={isDisabled}
        aria-label={inputProps['aria-label'] ?? (inputProps['aria-labelledby'] ? undefined : t('sliderValue'))}
        className={styles.input}
        onChange={(event) => { if (value === undefined) setInternalValue(Number(event.target.value)); onChange?.(event); }}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          if (isDisabled || event.defaultPrevented || event.button !== 0 || drag.current) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const computed = getComputedStyle(event.currentTarget);
          const thumb = parseFloat(computed.getPropertyValue('--bar-size')) || 22;
          const travel = Math.max(1, bounds.width - thumb);
          const rtl = computed.direction === 'rtl';
          const physicalPosition = rtl ? 1 - position : position;
          const center = bounds.left + thumb / 2 + physicalPosition * travel;
          const offset = Math.abs(event.clientX - center) <= thumb / 2 ? event.clientX - center : 0;
          drag.current = {pointerId: event.pointerId, left: bounds.left + thumb / 2, travel, offset, rtl, position};
          surface.current?.setAttribute('data-dragging', 'true');
          // The native range owns its drag capture. Capturing the pointer here
          // stops WebKit from advancing the input value while the thumb moves.
          moveDrag(event.clientX);
        }}
        onPointerMove={(event) => {
          if (drag.current?.pointerId === event.pointerId) moveDrag(event.clientX);
          onPointerMove?.(event);
        }}
        onPointerUp={(event) => { if (drag.current?.pointerId === event.pointerId) finishDrag(); onPointerUp?.(event); }}
        onPointerCancel={(event) => { if (drag.current?.pointerId === event.pointerId) finishDrag(); onPointerCancel?.(event); }}
        onLostPointerCapture={(event) => { if (drag.current?.pointerId === event.pointerId) finishDrag(); onLostPointerCapture?.(event); }}
        onBlur={(event) => { finishDrag(); onBlur?.(event); }} />
    </span>
  </span>;
});

interface AnimatedProgressProps extends HTMLAttributes<HTMLSpanElement> {
  value?: number;
  max?: number;
  indeterminate?: boolean;
  state?: 'active' | 'error' | 'paused';
}

export function AnimatedProgress({ value = 0, max = 100, indeterminate = false, state = 'active', className, style, ...props }: AnimatedProgressProps) {
  const t = useTranslations('common');
  const position = barPosition(value, 0, max);
  return <span {...props} role="progressbar" aria-label={props['aria-label'] ?? (props['aria-labelledby'] ? undefined : t('progress'))}
    aria-valuemin={0} aria-valuemax={max} aria-valuenow={indeterminate ? undefined : position * max}
    className={cn('relative', styles.progress, className)} style={barStyle(position, style)}
    data-max={state === 'active' && !indeterminate && position === 1} data-indeterminate={indeterminate || undefined} data-state={state}>
    {indeterminate ? <SystemLoadingSurface as="span" className="system-loading-compact absolute inset-0 rounded-full" aria-hidden="true" /> : <BarTrack position={position} ticks={0} />}
  </span>;
}
